/**
 * How a table is kept in step with something outside this instance.
 *
 * Three things in one dialog, because they are one question: what it is
 * connected to, which of the source's properties become columns, and when it
 * last ran. The last of those is the one people open this for -- a sync that
 * stopped working is invisible from the table itself, where the rows simply
 * stop changing.
 *
 * There is no *making* a synced table here. That endpoint creates a table, and
 * this fork has no interface path for creating one at all -- tables come from
 * the API and the MCP tools. What it does have is tables that were created that
 * way and then had nothing to look after them.
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { whenText } from '@/shared/lib/when'
import { Button } from '@/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { NativeSelect } from '@/shared/ui/native-select'
import { Separator } from '@/shared/ui/separator'
import { Skeleton } from '@/shared/ui/skeleton'
import { Switch } from '@/shared/ui/switch'
import { settingsBody, settingsFor, syncState } from './types'
import {
  useDataSync,
  useDataSyncProperties,
  useSyncNow,
  useUpdateDataSync,
} from './queries'

export function DataSyncDialog({
  dataSyncId,
  tableId,
  tableName,
  onClose,
}: {
  dataSyncId: number
  tableId: number
  tableName: string
  onClose: () => void
}) {
  const { t } = useTranslation()
  const sync = useDataSync(dataSyncId)
  const properties = useDataSyncProperties(dataSyncId)
  const save = useUpdateDataSync(dataSyncId, tableId)
  const now = useSyncNow(dataSyncId)

  const [values, setValues] = useState<Record<string, string> | null>(null)
  const [synced, setSynced] = useState<string[] | null>(null)
  const [autoAdd, setAutoAdd] = useState(false)
  const [twoWay, setTwoWay] = useState(false)

  const held = sync.data
  useEffect(() => {
    if (held === undefined || values !== null) return
    const initial: Record<string, string> = {}
    for (const setting of settingsFor(held.type)) {
      // A secret never comes back, so its box opens empty and an empty box
      // means "leave it alone" -- see `isSecret`.
      const current = (held as unknown as Record<string, unknown>)[setting.name]
      // Only the shapes a setting box can hold. Anything else is a value this
      // form has no control for, and showing `[object Object]` in a text box
      // would invite somebody to save it back as that string.
      initial[setting.name] =
        typeof current === 'string' || typeof current === 'number' ? String(current) : ''
    }
    setValues(initial)
    setSynced(held.synced_properties.map((property) => property.key))
    setAutoAdd(held.auto_add_new_properties ?? false)
    setTwoWay(held.two_way_sync ?? false)
  }, [held, values])

  const state = held === undefined ? null : syncState(held)

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent className="flex max-h-[80vh] max-w-lg flex-col">
        <DialogHeader>
          <DialogTitle>{t('dataSync.title')}</DialogTitle>
          <DialogDescription>
            {t('dataSync.description', { table: tableName })}
          </DialogDescription>
        </DialogHeader>

        {state !== null && (
          <div className="flex items-center justify-between gap-2">
            <p
              className={
                state.kind === 'failed'
                  ? 'text-sm text-destructive'
                  : 'text-sm text-muted-foreground'
              }
            >
              {state.kind === 'never'
                ? t('dataSync.never')
                : state.kind === 'failed'
                  ? t('dataSync.failed', { error: state.error })
                  : t('dataSync.lastSynced', { when: whenText(state.at) })}
            </p>
            <Button
              size="sm"
              variant="outline"
              disabled={now.isPending}
              onClick={() => now.mutate()}
            >
              {t('dataSync.syncNow')}
            </Button>
          </div>
        )}
        {now.isSuccess && (
          <p className="text-xs text-muted-foreground">{t('dataSync.syncing')}</p>
        )}

        <Separator />

        {held === undefined || values === null || synced === null ? (
          <Skeleton className="h-40" />
        ) : (
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto">
            <section className="space-y-3" aria-label={t('dataSync.connection')}>
              <h3 className="text-sm font-medium">{t('dataSync.connection')}</h3>
              {settingsFor(held.type).map((setting) => (
                <div key={setting.name} className="grid gap-1">
                  <Label htmlFor={setting.name} className="text-xs text-muted-foreground">
                    {t(`dataSync.settings.${setting.name}`)}
                  </Label>
                  {setting.kind === 'choice' ? (
                    <NativeSelect
                      id={setting.name}
                      value={values[setting.name] ?? ''}
                      onChange={(event) =>
                        setValues({ ...values, [setting.name]: event.target.value })
                      }
                    >
                      {(setting.choices ?? []).map((choice) => (
                        <option key={choice} value={choice}>
                          {choice}
                        </option>
                      ))}
                    </NativeSelect>
                  ) : (
                    <Input
                      id={setting.name}
                      type={setting.kind === 'number' ? 'number' : setting.kind}
                      value={values[setting.name] ?? ''}
                      placeholder={
                        setting.kind === 'password' ? t('dataSync.unchanged') : undefined
                      }
                      onChange={(event) =>
                        setValues({ ...values, [setting.name]: event.target.value })
                      }
                    />
                  )}
                </div>
              ))}
            </section>

            <Separator />

            <section className="space-y-2" aria-label={t('dataSync.properties')}>
              <h3 className="text-sm font-medium">{t('dataSync.properties')}</h3>
              {/*
                Asked of the source rather than of this instance, so it can fail
                on its own while the settings above still read back -- which is
                exactly the state somebody opens this in when a connection has
                broken.
              */}
              {properties.isError ? (
                <p className="text-sm text-destructive">
                  {t('dataSync.propertiesFailed')}
                </p>
              ) : properties.isPending ? (
                <Skeleton className="h-24" />
              ) : (
                <ul className="space-y-2">
                  {(properties.data ?? []).map((property) => (
                    <li
                      key={property.key}
                      className="flex items-center justify-between gap-2"
                    >
                      <Label
                        htmlFor={`property-${property.key}`}
                        className="text-sm font-normal"
                      >
                        {property.name}
                      </Label>
                      {/*
                        A property the source identifies rows by cannot be
                        dropped: without it a sync has no way to tell one row
                        from another, so the backend keeps it whatever is sent.
                      */}
                      <Switch
                        id={`property-${property.key}`}
                        disabled={property.unique_primary}
                        checked={property.unique_primary || synced.includes(property.key)}
                        onCheckedChange={(on) =>
                          setSynced(
                            on
                              ? [...synced, property.key]
                              : synced.filter((key) => key !== property.key),
                          )
                        }
                      />
                    </li>
                  ))}
                </ul>
              )}

              <div className="flex items-center justify-between pt-2">
                <Label htmlFor="auto-add" className="text-sm font-normal">
                  {t('dataSync.autoAdd')}
                </Label>
                <Switch id="auto-add" checked={autoAdd} onCheckedChange={setAutoAdd} />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="two-way" className="text-sm font-normal">
                  {t('dataSync.twoWay')}
                </Label>
                <Switch id="two-way" checked={twoWay} onCheckedChange={setTwoWay} />
              </div>
            </section>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('action.cancel')}
          </Button>
          <Button
            disabled={held === undefined || values === null || save.isPending}
            onClick={() => {
              if (held === undefined || values === null || synced === null) return
              save.mutate(
                {
                  ...settingsBody(held.type, values),
                  synced_properties: synced,
                  auto_add_new_properties: autoAdd,
                  two_way_sync: twoWay,
                },
                { onSuccess: onClose },
              )
            }}
          >
            {t('action.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
